import {testIdProps} from '@github-ui/test-id-props'
import {clsx} from 'clsx'
import {useMemo, useRef, type ReactNode, type RefObject} from 'react'
import {TaillightContext, type TaillightBlockContext} from './taillight-context'
import type {TaillightProps} from './taillight-types'
import {TaillightsGlyphs} from './taillights-glyphs'
import {useTaillightsController, useTaillightsRendering} from './taillights-provider'
import {taillightsRenderingStyle} from './taillights-rendering-styles'
import styles from './taillight.module.css'

/**
 * Provides shared block layout and child context, adding a native text layer beside bitmap paint
 * for selection and accessibility when needed.
 */
export function TaillightFrame({
  block,
  renderer = 'bitmap',
  inert = false,
  isSelectable = false,
  hidePaint = false,
  children,
  controls,
  className,
  style,
  'aria-label': ariaLabel,
  nativeSourceRef: externalNativeSourceRef,
}: Omit<TaillightProps, 'children'> & {
  children: ReactNode
  controls?: ReactNode
  nativeSourceRef?: RefObject<HTMLPreElement | null>
}) {
  const controller = useTaillightsController()
  const rendering = useTaillightsRendering()
  const profile = controller?.profile
  const localNativeSourceRef = useRef<HTMLPreElement>(null)
  const nativeSourceRef = externalNativeSourceRef ?? localNativeSourceRef
  const accessibleName = ariaLabel?.trim() || undefined
  const separateNativeSource = renderer === 'bitmap' && (isSelectable || !inert)
  const context = useMemo<TaillightBlockContext>(
    // eslint-disable-next-line @github-ui/github-monorepo/no-mixed-stability-context-value -- Controls need both the source ref and block metadata, so splitting would not prevent their updates. Image readiness stays separate.
    () => ({block, nativeSourceRef, renderer, inert, isSelectable, hidePaint}),
    [block, nativeSourceRef, renderer, inert, isSelectable, hidePaint],
  )
  return (
    <TaillightContext value={context}>
      <div
        {...testIdProps('taillight')}
        className={clsx(styles.Block, className)}
        style={{
          ...style,
          ...taillightsRenderingStyle({...rendering, ...profile, ...block.palette}),
          '--taillight-height': `${block.height}px`,
          minWidth: block.width,
          '--taillights-default-color': block.palette.defaultColor,
        }}
      >
        {children}
        {separateNativeSource && (
          <pre
            {...testIdProps('taillight-native-source')}
            ref={nativeSourceRef}
            translate="no"
            className={clsx(styles.Selectable, !isSelectable && styles.nonSelectable, hidePaint && styles.hiddenPaint)}
            inert={inert || undefined}
            role={!inert && accessibleName ? 'region' : undefined}
            aria-label={!inert ? accessibleName : undefined}
            aria-hidden={inert || hidePaint || undefined}
            tabIndex={!inert && !hidePaint && isSelectable && accessibleName ? 0 : undefined}
          >
            <TaillightsGlyphs text={block.text} runs={block.glyphRuns} pseudoText={false} />
            {block.text.endsWith('\n') && <br inert aria-hidden />}
          </pre>
        )}
        {controls}
      </div>
    </TaillightContext>
  )
}
